/* =====================================================================
 * Admin usage dashboard API client — 본부 / 부서별 토큰 사용량.
 *
 * Tenant-wide, unlike `dashboard.ts` which the server scopes to the
 * caller. Access is an allowlist of (부서코드, 식별자) pairs held
 * server-side; there is no client-side parameter that widens or narrows
 * it. See `backend/app/domains/workspace/admin_dashboard_router.py`.
 *
 * Aggregates only — no prompt text and no per-run rows.
 * ===================================================================== */

import { authFetch, getHeaders, API_BASE_URL } from './authFetch';
import { DashboardUnavailableError } from './dashboard';

const ADMIN_DASHBOARD_PREFIX = `${API_BASE_URL}/api/v1/admin/dashboard`;

/** Adds `year` to the personal dashboard's day/week/month. */
export type AdminDashboardGranularity = 'day' | 'week' | 'month' | 'year';

/** Longest window the server accepts, mirroring `MAX_RANGE_DAYS`. */
export const ADMIN_DASHBOARD_MAX_RANGE_DAYS = 1830;

export interface AdminDashboardAccess {
  allowed: boolean;
  /** Only populated when `allowed` — otherwise the response reveals nothing. */
  department_code?: string | null;
  org_code?: string | null;
}

export interface AdminDashboardTotals {
  total_token_count: number;
  total_runs: number;
  /** Runs that carry a token snapshot. */
  token_run_count: number;
  /** Runs recorded before token capture — counted, never summed as zero. */
  unknown_token_run_count: number;
}

export interface AdminDashboardSummary extends AdminDashboardTotals {
  window_days: number;
  start: string;
  end: string;
  /**
   * Sum of each department's distinct-user count. Not a tenant-wide
   * distinct count: somebody who transferred mid-window appears in both
   * departments, which is correct per-department and inflated in total.
   */
  active_user_count_by_department_sum: number;
  org_count: number;
  department_count: number;
  avg_token_per_run: number | null;
  /** Share of runs with a token snapshot, 0–1. Null when there are no runs. */
  token_coverage: number | null;
  previous: AdminDashboardTotals & { start: string; end: string };
}

export interface AdminDashboardDepartment extends AdminDashboardTotals {
  department_code: string;
  department_name: string | null;
  user_count: number;
  avg_token_per_run: number | null;
  token_coverage: number | null;
}

export interface AdminDashboardOrg extends AdminDashboardTotals {
  org_code: string;
  org_name: string | null;
  user_count: number;
  avg_token_per_run: number | null;
  token_coverage: number | null;
  departments: AdminDashboardDepartment[];
}

export interface AdminDashboardBreakdown {
  start: string;
  end: string;
  total_token_count: number;
  total_runs: number;
  orgs: AdminDashboardOrg[];
}

export interface AdminSeriesPoint {
  /** ISO date of the bucket's first day, whatever the granularity. */
  bucket: string;
  total_token_count: number;
  total_runs: number;
  tokens_by_group: Record<string, number>;
  runs_by_group: Record<string, number>;
  token_run_count: number;
  unknown_token_run_count: number;
}

export interface AdminDashboardSeries {
  granularity: AdminDashboardGranularity;
  start: string;
  end: string;
  /** Which dimension `*_by_group` keys refer to. */
  split_by: 'org_code' | 'department_code';
  /** Ordered by size, so colour assignment is stable across renders. */
  groups: { code: string; name: string | null }[];
  total_token_count: number;
  total_runs: number;
  points: AdminSeriesPoint[];
}

/** One bucket of a single department's series, for the Excel export. */
export interface AdminExportPoint {
  bucket: string;
  total_token_count: number;
  total_runs: number;
}

export interface AdminExportDepartment extends AdminDashboardTotals {
  org_code: string;
  org_name: string | null;
  department_code: string;
  department_name: string | null;
  avg_token_per_run: number | null;
  points: AdminExportPoint[];
}

/**
 * Every department's series in one response.
 *
 * Carries no `user_count`: a distinct-user count cannot be derived by
 * summing per-day rows, so `/breakdown` owns that number.
 */
export interface AdminDashboardExport {
  granularity: AdminDashboardGranularity;
  start: string;
  end: string;
  total_token_count: number;
  total_runs: number;
  departments: AdminExportDepartment[];
}

export interface AdminDashboardDateRange {
  /** Inclusive KST calendar date (YYYY-MM-DD). */
  start: string;
  /** Inclusive KST calendar date (YYYY-MM-DD). */
  end: string;
}

/** Thrown when the caller is not on the allowlist (HTTP 403). */
export class AdminDashboardForbiddenError extends Error {
  constructor() {
    super('admin_dashboard_forbidden');
    this.name = 'AdminDashboardForbiddenError';
  }
}

async function get<T>(
  path: string,
  params?: Record<string, string | number | undefined>,
): Promise<T> {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === '') continue;
    qs.set(key, String(value));
  }
  const query = qs.toString();
  const res = await authFetch(
    `${ADMIN_DASHBOARD_PREFIX}${path}${query ? `?${query}` : ''}`,
    { method: 'GET', headers: getHeaders() },
  );
  if (res.status === 503) {
    throw new DashboardUnavailableError();
  }
  if (res.status === 403) {
    // Separated from a generic failure so the page can say "권한이 없습니다"
    // instead of rendering an empty chart that reads as "사용량 없음".
    throw new AdminDashboardForbiddenError();
  }
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  }
  return (await res.json()) as T;
}

/**
 * Whether the caller may open the admin dashboard.
 *
 * Answers 200 with `allowed: false` rather than 403, so the sidebar can
 * hide the entry without having to tell a rejection apart from an expired
 * session.
 */
export function fetchAdminDashboardAccess(): Promise<AdminDashboardAccess> {
  return get<AdminDashboardAccess>('/access');
}

export function fetchAdminDashboardSummary(
  days = 30,
): Promise<AdminDashboardSummary> {
  return get<AdminDashboardSummary>('/summary', { days });
}

export function fetchAdminDashboardBreakdown(
  days = 30,
  range?: AdminDashboardDateRange,
): Promise<AdminDashboardBreakdown> {
  return get<AdminDashboardBreakdown>(
    '/breakdown',
    range ? { start: range.start, end: range.end } : { days },
  );
}

export function fetchAdminDashboardSeries(
  granularity: AdminDashboardGranularity = 'day',
  days = 30,
  options?: {
    range?: AdminDashboardDateRange;
    orgCode?: string;
    departmentCode?: string;
  },
): Promise<AdminDashboardSeries> {
  return get<AdminDashboardSeries>('/series', {
    granularity,
    ...(options?.range
      ? { start: options.range.start, end: options.range.end }
      : { days }),
    org_code: options?.orgCode,
    department_code: options?.departmentCode,
  });
}

/**
 * Per-department series for the Excel export.
 *
 * Unscoped on purpose — the export writes one sheet per department, and
 * calling `/series` once per department would issue as many requests as
 * there are departments, each rescanning the same window.
 */
export function fetchAdminDashboardExport(
  granularity: AdminDashboardGranularity = 'day',
  days = 30,
  range?: AdminDashboardDateRange,
): Promise<AdminDashboardExport> {
  return get<AdminDashboardExport>('/export', {
    granularity,
    ...(range ? { start: range.start, end: range.end } : { days }),
  });
}
